import { AnanseApiService } from "app/services/Ananse/AnanseApiService";
import { CommercialAddress, CustomerSource } from "../types/proposal";

export type CustomerSearchScope = "all" | "user" | "asaas";

export type CustomerSearchResult = {
  key: string;
  source: Exclude<CustomerSource, "manual">;
  sourceId: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  document: string;
  address: CommercialAddress;
};

type UnknownRecord = Record<string, any>;

const api = new AnanseApiService();

const stringValue = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

const emptyAddress = (): CommercialAddress => ({
  zipCode: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
});

const responseRows = (response: any): UnknownRecord[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.customers)) return response.customers;
  return [];
};

const mapUser = (user: UnknownRecord): CustomerSearchResult => {
  const address = user.address ?? {};
  return {
    key: `user-${user.id}`,
    source: "user",
    sourceId: stringValue(user.id),
    name: stringValue(user.nome ?? user.name),
    company: stringValue(user.company ?? user.empresa),
    email: stringValue(user.email),
    phone: stringValue(user.mobilePhone ?? user.phone ?? user.telefone),
    document: stringValue(user.cpf ?? user.document ?? user.tin),
    address: {
      ...emptyAddress(),
      zipCode: stringValue(address.zipcode ?? address.zipCode ?? user.zipcode),
      street: stringValue(address.street ?? address.address ?? user.street),
      number: stringValue(address.number ?? user.addressNumber),
      complement: stringValue(address.complement ?? user.complement),
      neighborhood: stringValue(address.neighborhood ?? address.province),
      city: stringValue(address.city),
      state: stringValue(address.state ?? address.region),
    },
  };
};

const mapAsaasCustomer = (customer: UnknownRecord): CustomerSearchResult => {
  const city = customer.city;
  return {
    key: `asaas-${customer.id}`,
    source: "asaas",
    sourceId: stringValue(customer.id),
    name: stringValue(customer.name),
    company: stringValue(customer.company ?? customer.companyName),
    email: stringValue(customer.email),
    phone: stringValue(customer.mobilePhone ?? customer.phone),
    document: stringValue(customer.cpfCnpj ?? customer.document),
    address: {
      ...emptyAddress(),
      zipCode: stringValue(customer.postalCode ?? customer.zipCode),
      street: stringValue(customer.address),
      number: stringValue(customer.addressNumber),
      complement: stringValue(customer.complement),
      neighborhood: stringValue(customer.province ?? customer.neighborhood),
      city: stringValue(typeof city === "object" ? city?.name : city),
      state: stringValue(customer.state ?? customer.uf),
    },
  };
};

const searchUsers = async (query: string): Promise<CustomerSearchResult[]> => {
  const response = await api.makeHttpRequest({
    method: "GET",
    url: "/usuario",
    params: {
      Page: 1,
      PerPage: 10,
      OrderBy: "Nome",
      OrderByDirection: "asc",
      IsActive: true,
      FilterString: query,
      FilterType: query.includes("@") ? "Email" : "Nome",
    },
  });
  return responseRows(response).map(mapUser);
};

const searchAsaasCustomers = async (
  query: string,
): Promise<CustomerSearchResult[]> => {
  const response = await api.makeHttpRequest({
    method: "GET",
    url: "/asaaswebhook/test-customers",
    params: {
      page: 1,
      perPage: 10,
      FilterString: query,
    },
  });
  return responseRows(response).map(mapAsaasCustomer);
};

export const searchCommercialCustomers = async (
  query: string,
  scope: CustomerSearchScope,
): Promise<{
  results: CustomerSearchResult[];
  unavailableSources: CustomerSource[];
}> => {
  const searches: Array<{
    source: Exclude<CustomerSource, "manual">;
    request: Promise<CustomerSearchResult[]>;
  }> = [];

  if (scope === "all" || scope === "user") {
    searches.push({ source: "user", request: searchUsers(query) });
  }
  if (scope === "all" || scope === "asaas") {
    searches.push({ source: "asaas", request: searchAsaasCustomers(query) });
  }

  const responses = await Promise.all(
    searches.map(async search => {
      try {
        return {
          source: search.source,
          results: await search.request,
          available: true,
        };
      } catch {
        return { source: search.source, results: [], available: false };
      }
    }),
  );
  const results: CustomerSearchResult[] = [];
  const unavailableSources: CustomerSource[] = [];

  responses.forEach(response => {
    results.push(...response.results);
    if (!response.available) unavailableSources.push(response.source);
  });

  return { results, unavailableSources };
};
